import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/app/session';
import { Button, Card, DataTable, Icon, Pill, StatCard, type Column, type Tone } from '@/design-system';
import { now } from '@/lib/clock';
import { num } from '@/api/types';
import { daysUntil, formatCompactSum, formatDateRange, formatDateShort, formatDeltaPct, formatNumber, formatSum, formatUsd, monthName } from '@/lib/format';
import { discountLabel, monthly, planName } from '@/lib/subscriptions';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { LoadError } from '@/shell/LoadError';
import { PageHeader } from '@/shell/PageHeader';
import { plansQuery, subscriptionsQuery, subscriptionsReportQuery, type Plan, type Subscription, type SubscriptionsReport } from './api';
import { PlanEditor } from './PlanEditor';
import s from './plans.module.css';

const EXPIRING_DAYS = 7;

export function PlansPage() {
  const { t } = useTranslation('plans');
  useBreadcrumbs([{ label: t('title') }]);
  const report = useQuery(subscriptionsReportQuery);
  const plans = useQuery(plansQuery);
  const subs = useQuery(subscriptionsQuery);
  const failed = [report, plans, subs].find((q) => q.isError);

  return (
    <>
      <PageHeader row eyebrow={t('eyebrow')} title={t('title')} />
      {failed ? (
        <LoadError error={failed.error} onRetry={() => [report, plans, subs].forEach((q) => void q.refetch())} />
      ) : (
        <>
          {report.data && <Kpis report={report.data} />}
          {plans.data && <PlanCards plans={plans.data} />}
          {subs.data && <SubscriptionsTable rows={subs.data} />}
        </>
      )}
    </>
  );
}

/** The UZS amount of a per-currency list (the API reports MRR/ARR per currency). */
function uzs(list: { currency: string; amount: string }[] | null | undefined): number | null {
  if (!list) return null;
  return num(list.find((a) => a.currency === 'UZS')?.amount ?? 0);
}

function Kpis({ report }: { report: SubscriptionsReport }) {
  const { t } = useTranslation('plans');
  const { t: tc } = useTranslation('common');
  const today = now();
  const month = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const prevMonth = `${today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear()}-${String(((today.getMonth() + 11) % 12) + 1).padStart(2, '0')}`;
  const mrrValue = uzs(report.mrr) ?? 0;
  const prev = uzs(report.previous_mrr);
  const mrr = formatCompactSum(mrrValue);
  const arr = formatCompactSum(uzs(report.arr) ?? 0);
  const sites = report.active_sites as { production?: number | null; demo?: number | null; landing?: number | null } | null | undefined;
  const dash = tc('dash');
  const n = (v: number | null | undefined) => (typeof v === 'number' ? formatNumber(v) : dash);
  const gens = report.generations_month;
  const edits = report.edits_month;
  return (
    <div className={s.grid}>
      <StatCard
        label={t('kpi.mrr')}
        value={mrr.value}
        unit={mrr.unit}
        sub={prev ? t('kpi.mrr_delta', { delta: formatDeltaPct(mrrValue, prev), month: monthName(prevMonth, 'dative') }) : dash}
        subTone="accent"
      />
      <StatCard label={t('kpi.arr')} value={arr.value} unit={arr.unit} sub={t('kpi.paying', { count: report.paying_organizations ?? 0 })} />
      <StatCard
        label={t('kpi.ai', { month: monthName(month) })}
        value={report.ai_spend_usd_month === null || report.ai_spend_usd_month === undefined ? dash : formatUsd(num(report.ai_spend_usd_month))}
        sub={
          gens === null || gens === undefined || edits === null || edits === undefined
            ? dash
            : t('kpi.ai_sub', {
                generations: t('kpi.generations', { count: gens, n: formatNumber(gens) }),
                edits: t('kpi.edits', { count: edits, n: formatNumber(edits) }),
              })
        }
      />
      <StatCard
        label={t('kpi.sites')}
        value={sites ? formatNumber((sites.production ?? 0) + (sites.demo ?? 0) + (sites.landing ?? 0)) : dash}
        sub={sites ? t('kpi.sites_sub', { production: n(sites.production), demo: n(sites.demo), landing: n(sites.landing) }) : dash}
      />
    </div>
  );
}

function PlanCards({ plans }: { plans: Plan[] }) {
  const { t } = useTranslation('plans');
  const { isAdmin } = useSession();
  const [editing, setEditing] = useState<Plan | null>(null);
  return (
    <div className={s.grid}>
      {plans.map((p) => (
        <Card key={p.id} texture={p.highlighted ? 'highlight' : 'plain'} className={s.plan}>
          <div className={s.planHead}>
            <span className={s.planName}>{p.name}</span>
            <span className={s.planSubs}>{t('plan.subs', { count: p.subscriptions_count ?? 0 })}</span>
          </div>
          <div className={s.priceBlock}>
            <span className={s.price}>{formatSum(num(p.price))}</span>
            <span className={s.per}>{t('plan.per')}</span>
          </div>
          <ul className={s.features}>
            {p.features.map((f) => (
              <li key={f} className={s.feature}>
                <Icon name="check" size={14} className={s.check} />
                {f}
              </li>
            ))}
          </ul>
          {isAdmin && (
            <Button variant="secondary" size={38} className={s.edit} onClick={() => setEditing(p)}>
              {t('plan.edit')}
            </Button>
          )}
        </Card>
      ))}
      {editing && <PlanEditor plan={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function paymentPill(sub: Subscription, t: (k: string, o?: Record<string, unknown>) => string): { tone: Tone; label: string } {
  if (sub.status === 'trial') return { tone: 'mute', label: t('subs.trial') };
  if (sub.payment_status === 'overdue') return { tone: 'err', label: t('subs.overdue') };
  if (sub.payment_status === 'awaiting') return { tone: 'acc', label: t('subs.awaiting') };
  if (sub.payment_status === 'paid') {
    const left = sub.ends_on ? daysUntil(sub.ends_on, now()) : -1;
    if (left >= 0 && left <= EXPIRING_DAYS) return { tone: 'warn', label: t('subs.expiring', { count: left }) };
    return { tone: 'ok', label: t('subs.paid') };
  }
  return { tone: 'mute', label: t('subs.unknown') };
}

function SubscriptionsTable({ rows }: { rows: Subscription[] }) {
  const { t } = useTranslation('plans');
  const { t: tc } = useTranslation('common');
  const tt = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const period = (x: Subscription) => {
    if (x.status === 'trial') {
      const until = x.trial_until ?? x.ends_on;
      return until ? t('subs.days', { count: daysUntil(until, new Date(`${x.starts_on}T12:00:00+05:00`)) }) : tc('dash');
    }
    return x.period === 'yearly' ? t('subs.year') : t('subs.month');
  };
  const columns: Column<Subscription>[] = [
    { key: 'org', header: t('subs.organization'), width: 'minmax(0,1.3fr)', render: (x) => <span className={s.org}>{x.organization.brand_name}</span> },
    { key: 'plan', header: t('subs.plan'), width: 'minmax(0,.7fr)', render: (x) => <span className={s.muted}>{planName(x)}</span> },
    { key: 'period', header: t('subs.period'), width: 'minmax(0,.7fr)', render: (x) => <span className={s.muted}>{period(x)}</span> },
    { key: 'price', header: t('subs.price'), width: 'minmax(0,.9fr)', render: (x) => <span className={s.num}>{formatSum(monthly(x.effective_price, x.period))}</span> },
    { key: 'override', header: t('subs.override'), width: 'minmax(0,1.3fr)', render: (x) => <span className={s.override}>{discountLabel(x, tt) ?? tc('dash')}</span> },
    {
      key: 'term',
      header: t('subs.term'),
      width: 'minmax(0,1.3fr)',
      render: (x) => <span className={s.term}>{x.ends_on ? formatDateRange(x.starts_on, x.ends_on) : formatDateShort(x.starts_on)}</span>,
    },
    {
      key: 'pay',
      header: t('subs.payment'),
      width: 'minmax(0,1.1fr)',
      render: (x) => {
        const p = paymentPill(x, tt);
        return (
          <Pill tone={p.tone} size="sm" start>
            {p.label}
          </Pill>
        );
      },
    },
  ];
  return <DataTable label={t('subs.title')} title={t('subs.title')} compact rows={rows} rowKey={(x) => x.id} columns={columns} empty={t('subs.empty')} />;
}
